import { IonButton } from '@ionic/react';
import { App as CapacitorApp } from '@capacitor/app';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAuthStateTransformed } from 'appRedux/selectors';
import {
  captureDebugLimitFromUrl,
  ensureSession,
  formatRemaining,
  getSessionStatus,
  readSession,
  recordActivity,
  requestInactivityLogout,
} from 'lib/sessionExpiry';

/**
 * The app's one inactivity tracker (replaces the old 20-minute App.tsx timer and the
 * 60-minute useIdleTimer in SideMenu). Mounted once in App.tsx.
 *
 * - Activity: touchstart, pointerdown, keydown and scroll, all on document in the
 *   capture phase so scrolling inside drawers / ion-content counts. Writes of
 *   lastActivityAt are throttled (lib/sessionExpiry). API calls are not activity.
 * - Checks: on mount, every second, on visibilitychange / focus and on Capacitor App
 *   resume. Because the time is read from localStorage, a relaunch or a phone that was
 *   locked for hours is caught on the first check.
 * - Warning: shows the time left with 'Stay logged in' (counts as activity) and
 *   'Log out'. While it is up, other touches don't extend the session.
 * - Expired: full logout through /logout (Auth.signOut + clearState).
 */

const ACTIVITY_EVENTS = ['touchstart', 'pointerdown', 'keydown', 'scroll'] as const;
const TICK_MS = 1000;

export const SessionTimeoutWarning = () => {
  const isAuthenticated = useSelector(selectAuthStateTransformed);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const warningVisibleRef = useRef(false);

  const showWarning = useCallback((ms: number | null) => {
    warningVisibleRef.current = ms != null;
    setRemainingMs(ms);
  }, []);

  const check = useCallback(() => {
    if (window.location.pathname === '/logout') return;
    const record = readSession();
    if (!record) {
      showWarning(null);
      return;
    }
    const status = getSessionStatus(Date.now(), record);
    if (status.phase === 'expired') {
      showWarning(null);
      requestInactivityLogout();
    } else if (status.phase === 'warning') {
      showWarning(status.remainingMs);
    } else if (warningVisibleRef.current) {
      // Activity in another tab (or 'Stay logged in') moved the deadline.
      showWarning(null);
    }
  }, [showWarning]);

  // Dev builds only: ?sessionDebugLimit=<seconds> shortens the limit for testing.
  useEffect(() => {
    captureDebugLimitFromUrl(window.location.search);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || window.location.pathname === '/logout') {
      showWarning(null);
      return undefined;
    }

    // A session signed in before this tracker shipped has no record yet: 1 hour from
    // now (GetMe raises it to 4 hours on casino entry when a link is verified).
    ensureSession(Date.now());
    check();

    const onActivity = () => {
      if (warningVisibleRef.current) return;
      if (!recordActivity(Date.now())) requestInactivityLogout();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    ACTIVITY_EVENTS.forEach((type) =>
      document.addEventListener(type, onActivity, { capture: true, passive: true })
    );
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);
    const interval = window.setInterval(check, TICK_MS);

    let removeResume: (() => void) | null = null;
    let disposed = false;
    try {
      const handle = CapacitorApp.addListener('resume', check);
      Promise.resolve(handle)
        .then((h) => {
          if (disposed) void h.remove();
          else removeResume = () => void h.remove();
        })
        .catch(() => undefined);
    } catch {
      /* not running in the native shell */
    }

    return () => {
      disposed = true;
      ACTIVITY_EVENTS.forEach((type) =>
        document.removeEventListener(type, onActivity, { capture: true })
      );
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
      window.clearInterval(interval);
      if (removeResume) removeResume();
    };
  }, [isAuthenticated, check, showWarning]);

  if (!isAuthenticated || remainingMs == null) return null;

  const onStay = () => {
    if (recordActivity(Date.now(), true)) showWarning(null);
    else requestInactivityLogout();
  };

  return (
    <div className="session-timeout-root" role="presentation">
      <div className="session-timeout__backdrop" aria-hidden="true" />
      <div
        className="session-timeout"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-text"
      >
        <h2 id="session-timeout-title" className="session-timeout__title">
          Are you still there?
        </h2>
        <p id="session-timeout-text" className="session-timeout__text">
          For your security, you&apos;ll be logged out in
        </p>
        <div className="session-timeout__time" aria-live="polite">
          {formatRemaining(remainingMs)}
        </div>
        <div className="session-timeout__actions">
          <IonButton expand="block" className="session-timeout__stay" onClick={onStay}>
            Stay logged in
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            className="session-timeout__logout"
            onClick={() => requestInactivityLogout()}
          >
            Log out
          </IonButton>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
